import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { SnackbarService } from '@tiltgroup/web-components';
import {
  auditTime,
  catchError,
  filter,
  fromEvent,
  map,
  merge,
  Observable,
  of,
} from 'rxjs';

interface AppVersionManifest {
  version: string;
  gitSha: string;
  builtAt: string;
  buildId: string;
}

const APP_BUILD_META_NAME = 'app-build';
const APP_BUILD_PLACEHOLDER = '__APP_BUILD__';
const VERSION_RELOAD_STORAGE_KEY = 'nx.app-version-reload.target';
const CHUNK_RELOAD_STORAGE_KEY = 'nx.app-version-reload.chunk';
const VERSION_CHECK_DEBOUNCE_MS = 1000;
const RELOAD_DELAY_MS = 1000;
/**
 * How long a newly deployed build must keep being reported before the reload
 * prompt. Staging and production roll out one pod at a time, and until the old
 * pod is gone `/version.json` can answer with either build.
 */
const VERSION_CONFIRM_DELAY_MS = 2 * 60 * 1000;

/**
 * The build id CI stamps into index.html (`<version>+<build number>`), or
 * `null` for local builds where the placeholder was never substituted.
 */
export function readAppBuildId(): string | null {
  const buildMeta = document.querySelector(
    `meta[name="${APP_BUILD_META_NAME}"]`
  );
  const buildId = buildMeta?.getAttribute('content')?.trim();

  if (!buildId || buildId === APP_BUILD_PLACEHOLDER) {
    return null;
  }

  return buildId;
}

/**
 * Whether `error` (an Error, a message, or an error-like object) is the
 * browser failing to load a lazy chunk that a newer deploy removed.
 */
export function isStaleBundleError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();

  if (!message) {
    return false;
  }

  return [
    'chunkloaderror',
    'loading chunk',
    'failed to fetch dynamically imported module',
    // Firefox
    'error loading dynamically imported module',
    'importing a module script failed',
  ].some((pattern) => message.includes(pattern));
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name} ${error.message}`.trim();
  }

  if (typeof error === 'string') {
    return error;
  }

  if (
    typeof error === 'object' &&
    error &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  return '';
}

@Injectable({
  providedIn: 'root',
})
export class AppVersionGuardService {
  private _destroyRef = inject(DestroyRef);
  private _httpClient = inject(HttpClient);
  private _router = inject(Router);
  private _snackbarService = inject(SnackbarService);

  readonly currentBuildId = readAppBuildId();
  private _initialized = false;
  // In-memory fallbacks: when sessionStorage is blocked by browser policy,
  // these still dedupe notifications/reloads within this page instance so
  // recovery keeps working instead of being disabled (or looping).
  private _notifiedBuildId: string | null = null;
  private _chunkReloadTriggered = false;
  private _chunkCheckInFlight = false;
  /** A deployed build seen once and awaiting its confirming check. */
  private _pendingBuildId: string | null = null;
  private _confirmationTimer: number | null = null;

  // Runs on every deployed build, development and feature included; local
  // builds have no build id and are skipped.
  init(): void {
    if (this._initialized || !this.currentBuildId) {
      return;
    }

    this._initialized = true;
    // The app booted, so the current bundle loaded fine — re-arm the one-shot
    // chunk-reload flag so the next deploy-induced stale-chunk error can
    // trigger an automatic reload again (the flag survives reloads within a
    // tab's sessionStorage, and would otherwise permanently disable recovery
    // after the first incident).
    this._safeRemoveItem(CHUNK_RELOAD_STORAGE_KEY);
    this._checkForUpdates();

    merge(
      fromEvent(window, 'focus'),
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'visible')
      ),
      this._router.events.pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
    )
      .pipe(
        auditTime(VERSION_CHECK_DEBOUNCE_MS),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => this._checkForUpdates());

    this._destroyRef.onDestroy(() => this._clearPendingBuild());

    this._router.events
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationError) {
          this._handlePotentialStaleBundleError(event.error);
        }
      });

    fromEvent<ErrorEvent>(window, 'error')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((event) =>
        this._handlePotentialStaleBundleError(event.error || event.message)
      );

    fromEvent<PromiseRejectionEvent>(window, 'unhandledrejection')
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((event) =>
        this._handlePotentialStaleBundleError(event.reason)
      );
  }

  /**
   * The build id `/version.json` reports, or `null` when it can't be read
   * (offline, a 5xx mid-deploy, blocked): nothing to act on or to report.
   */
  private _fetchDeployedBuildId(): Observable<string | null> {
    return this._httpClient
      .get<AppVersionManifest>(`/version.json?t=${Date.now()}`)
      .pipe(
        map((manifest) => manifest?.buildId?.trim() || null),
        catchError(() => of(null))
      );
  }

  private _checkForUpdates(): void {
    this._fetchDeployedBuildId().subscribe((buildId) =>
      this._handleDeployedBuildId(buildId, false)
    );
  }

  private _confirmPendingBuild(): void {
    this._confirmationTimer = null;
    this._fetchDeployedBuildId().subscribe((buildId) =>
      this._handleDeployedBuildId(buildId, true)
    );
  }

  /**
   * Prompts to reload once a different build is reported twice: when first
   * seen, and again by the check {@link VERSION_CONFIRM_DELAY_MS} later, with
   * no report of the running build in between. A single answer from a pod a
   * rollout hasn't replaced yet then never prompts.
   */
  private _handleDeployedBuildId(
    latestBuildId: string | null,
    confirming: boolean
  ): void {
    if (!latestBuildId) {
      // Keep waiting on a build already seen rather than drop it.
      if (confirming && this._pendingBuildId) {
        this._scheduleConfirmation();
      }
      return;
    }

    if (latestBuildId === this.currentBuildId) {
      this._clearPendingBuild();
      return;
    }

    if (
      this._notifiedBuildId === latestBuildId ||
      this._safeGetItem(VERSION_RELOAD_STORAGE_KEY) === latestBuildId
    ) {
      return;
    }

    if (confirming && latestBuildId === this._pendingBuildId) {
      this._clearPendingBuild();
      this._promptReload(latestBuildId);
      return;
    }

    if (latestBuildId !== this._pendingBuildId) {
      this._pendingBuildId = latestBuildId;
      this._scheduleConfirmation();
    } else if (this._confirmationTimer === null) {
      this._scheduleConfirmation();
    }
  }

  private _scheduleConfirmation(): void {
    if (this._confirmationTimer !== null) {
      window.clearTimeout(this._confirmationTimer);
    }

    this._confirmationTimer = window.setTimeout(
      () => this._confirmPendingBuild(),
      VERSION_CONFIRM_DELAY_MS
    );
  }

  private _clearPendingBuild(): void {
    this._pendingBuildId = null;

    if (this._confirmationTimer !== null) {
      window.clearTimeout(this._confirmationTimer);
      this._confirmationTimer = null;
    }
  }

  private _promptReload(latestBuildId: string): void {
    this._notifiedBuildId = latestBuildId;
    this._safeSetItem(VERSION_RELOAD_STORAGE_KEY, latestBuildId);
    this._snackbarService.showNotificationWithAction(
      'Outdated application version detected. For stable usage experience reloading is recommended.',
      {
        actionText: 'Reload',
        actionCallback: () => {
          this._reloadPage();
        },
      },
      {
        duration: 0,
      },
      {
        name: 'nx:ic-alert-minor',
      }
    );
  }

  /**
   * Reloads once when a lazy chunk fails to load because a deploy removed it.
   * The same error also means offline or a blocked chunk, where a reload would
   * load nothing new and only lose what's on screen, so it reloads only when
   * `/version.json` reports a different build than the one running.
   */
  private _handlePotentialStaleBundleError(error: unknown): void {
    if (
      !isStaleBundleError(error) ||
      this._chunkReloadTriggered ||
      this._chunkCheckInFlight ||
      this._safeGetItem(CHUNK_RELOAD_STORAGE_KEY) === 'true'
    ) {
      return;
    }

    this._chunkCheckInFlight = true;
    this._fetchDeployedBuildId().subscribe((latestBuildId) => {
      this._chunkCheckInFlight = false;

      if (!latestBuildId || latestBuildId === this.currentBuildId) {
        return;
      }

      this._chunkReloadTriggered = true;
      this._safeSetItem(CHUNK_RELOAD_STORAGE_KEY, 'true');
      this._snackbarService.showNotification(
        'The application was updated. Refreshing...'
      );

      window.setTimeout(() => this._reloadPage(), RELOAD_DELAY_MS);
    });
  }

  private _reloadPage(): void {
    window.location.reload();
  }

  // sessionStorage access can throw when storage is blocked entirely by
  // browser policy. Recovery must keep working without it (deduped by the
  // in-memory fields above), so all storage access is best-effort.
  private _safeGetItem(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private _safeSetItem(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Best-effort only.
    }
  }

  private _safeRemoveItem(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Best-effort only.
    }
  }
}
